<template>
  <div class="min-h-[60vh] flex items-center justify-center animate-fade-in">
    <div class="glass rounded-xl p-6 w-[420px] max-w-[90vw]">
      <p class="text-slate-300 text-sm mb-3">
        In order to navigate TestDrive, please complete your 2FA enrollment.
      </p>
      <h2 class="text-xl font-semibold text-white mb-2">Two-Factor Authentication</h2>
      <p class="text-slate-400 text-sm mb-4">
        <span v-if="isVerifyMode">Verify your 2FA code to continue.</span>
        <span v-else>Enable TOTP to continue.</span>
      </p>

      <div v-if="!factorId && !isVerifyMode">
        <button @click="startEnroll" class="w-full px-3 py-2 btn-primary rounded-lg">
          Start Enrollment
        </button>
      </div>

      <div v-else-if="isVerifyMode && !qrSvg">
        <!-- Verification mode - user has TOTP, just needs to verify -->
        <div class="text-center mb-4">
          <p class="text-slate-300 text-sm mb-3">
            Enter your 6-digit authenticator code to continue
          </p>
        </div>
        <div class="flex space-x-2">
          <input
            v-model="verifyCode"
            placeholder="Enter 6-digit code"
            class="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            :disabled="isVerifying"
          />
          <button
            @click="verifyEnroll"
            class="px-4 py-2 bg-primary-600 text-white rounded-lg"
            :disabled="isVerifying"
          >
            {{ isVerifying ? 'Verifying...' : 'Verify' }}
          </button>
        </div>
        <!-- Debug buttons — only visible in development -->
        <div v-if="isDev" class="mt-2 text-center space-x-2">
          <button
            @click="quickVerify"
            class="px-3 py-1 text-xs bg-green-700 text-white rounded hover:bg-green-600"
          >
            Quick Verify
          </button>
          <button
            @click="forceAAL2"
            class="px-3 py-1 text-xs bg-red-700 text-white rounded hover:bg-red-600"
          >
            Force AAL2
          </button>
          <button
            @click="clearAndRestart"
            class="px-3 py-1 text-xs bg-orange-700 text-white rounded hover:bg-orange-600"
          >
            Clear & Restart
          </button>
        </div>
        <p v-if="mfaMessage" class="text-slate-400 mt-2">{{ mfaMessage }}</p>
      </div>

      <div v-else>
        <div v-if="qrSvg" class="bg-white p-3 rounded qr" aria-label="TOTP QR" v-html="qrSvg"></div>
        <p v-if="qrSvg" class="text-slate-400 text-xs mt-2">
          Tip: Open your authenticator app and use its “Scan QR code” option. Scanning with the
          phone camera alone may not open an authenticator.
        </p>
        <div class="flex space-x-2 mt-3">
          <input
            v-model="verifyCode"
            placeholder="Enter code"
            class="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            :disabled="isVerifying"
          />
          <button
            @click="verifyEnroll"
            class="px-4 py-2 bg-primary-600 text-white rounded-lg"
            :disabled="isVerifying"
          >
            {{ isVerifying ? 'Verifying...' : 'Verify' }}
          </button>
        </div>
        <p v-if="mfaMessage" class="text-slate-400 mt-2">{{ mfaMessage }}</p>

        <div v-if="totpSecret" class="mt-4 border-t border-slate-800 pt-3 text-sm text-slate-300">
          <p class="mb-1 font-medium">Can't scan the QR? Enter the setup key manually</p>
          <ul class="list-disc ml-5 space-y-1">
            <li>Account name: {{ currentEmail || 'your email' }}</li>
            <li>Type: Time-based (TOTP), 6 digits</li>
            <li>Setup key:</li>
          </ul>
          <div class="mt-2 flex items-center gap-2">
            <code
              class="px-2 py-1 bg-slate-900 rounded border border-slate-700 text-slate-200 select-all"
              >{{ totpSecret }}</code
            >
            <button
              @click="copySecret"
              class="px-2 py-1 text-xs bg-slate-800 border border-slate-700 rounded text-slate-200 hover:bg-slate-700"
            >
              Copy
            </button>
            <a v-if="otpauthUri" :href="otpauthUri" class="text-xs underline text-slate-200"
              >Open in authenticator</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { createLogger } from '@/lib/logger'

const log = createLogger('MFA')
const isDev = import.meta.env.DEV

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const factorId = ref('')
const qrSvg = ref('')
const verifyCode = ref('')
const mfaMessage = ref('')
const totpSecret = ref('')
const currentEmail = ref('')
const otpauthUri = ref('')
const isVerifyMode = ref(false)
const isVerifying = ref(false)

// Initialize: check auth state and determine what to show
;(async () => {
  try {
    log.debug('Initializing MFA view...')

    // First, check if user already has AAL2
    await auth.bootstrap()
    log.debug('Current auth state - AAL:', auth.aal, 'Session:', !!auth.session)

    if (auth.aal === 'aal2') {
      log.debug('User already has AAL2, redirecting to destination')
      const go = (route.query.redirect as string) || '/dashboard'
      router.push(go)
      return
    }

    // Check TOTP factors
    const lf = await supabase.auth.mfa.listFactors()
    const verified = (lf.data?.totp || []).find((f) => f.status === 'verified')

    log.debug('TOTP factors:', {
      hasVerified: !!verified,
      factorCount: lf.data?.totp?.length || 0,
    })

    if (verified) {
      factorId.value = verified.id
      isVerifyMode.value = true
      mfaMessage.value = 'Enter your 6-digit authenticator code to continue.'
      log.debug('Ready for verification with factorId:', verified.id)
    } else {
      isVerifyMode.value = false
      mfaMessage.value = 'Please set up 2FA first.'
      log.debug('No verified TOTP found, showing enrollment')
    }
  } catch (error) {
    log.warn('Failed to initialize:', error)
    mfaMessage.value = 'Error loading 2FA. Use emergency buttons below.'
  }
})()

async function startEnroll() {
  mfaMessage.value = ''
  try {
    // Inspect existing factors
    const lf0 = await supabase.auth.mfa.listFactors()
    const verified0 = (lf0.data?.totp || []).find((f) => f.status === 'verified')
    if (verified0) {
      factorId.value = verified0.id
      mfaMessage.value = 'Two-factor is already enabled. Enter a code to continue.'
      return
    }

    // Clean up ALL dangling unverified TOTP factors to avoid 422
    const toRemove = (lf0.data?.totp || []).filter((f) => f.status !== 'verified')
    await Promise.all(
      toRemove.map((f) => supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {})),
    )

    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' })
    if (error) {
      mfaMessage.value =
        error.message ||
        'Failed to start enrollment. If you previously started enrollment, try again in a moment.'
      return
    }
    factorId.value = data.id
    qrSvg.value = data.totp.qr_code
    // Secret is returned for manual entry but may not be typed in SDK definitions
    totpSecret.value = (data.totp as any).secret || ''
    try {
      const { data: userData } = await supabase.auth.getUser()
      currentEmail.value = userData.user?.email || ''
    } catch {}
    // Construct otpauth URI for deep link
    const label = encodeURIComponent(`TestDrive:${currentEmail.value || 'user'}`)
    const issuer = encodeURIComponent('TestDrive')
    otpauthUri.value = `otpauth://totp/${label}?secret=${totpSecret.value}&issuer=${issuer}&digits=6&period=30`
  } catch (e) {
    mfaMessage.value = e instanceof Error ? e.message : String(e)
  }
}

async function verifyEnroll() {
  if (!verifyCode.value.trim()) {
    mfaMessage.value = 'Please enter a 6-digit code'
    return
  }

  mfaMessage.value = 'Verifying...'
  isVerifying.value = true

  try {
    log.debug('Starting verification with factorId:', factorId.value)

    // Step 1: Create challenge (this should be fast)
    const challenge = await supabase.auth.mfa.challenge({ factorId: factorId.value })
    if (challenge.error) {
      log.error('Challenge error:', challenge.error)
      mfaMessage.value = challenge.error.message
      return
    }

    log.debug('Challenge created, challengeId:', challenge.data.id)

    // Step 2: Verify the code with timeout
    log.debug('Verifying code...')

    const verificationPromise = supabase.auth.mfa.verify({
      factorId: factorId.value,
      challengeId: challenge.data.id,
      code: verifyCode.value.trim(),
    })

    // Add timeout to prevent hanging on response processing
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Verification response timeout')), 5000),
    )

    const verification = await Promise.race([verificationPromise, timeoutPromise])

    if ((verification as any).error) {
      log.error('Verification error:', (verification as any).error)
      mfaMessage.value = (verification as any).error.message
      return
    }

    log.debug('Verification API call successful!')
    mfaMessage.value = '2FA verified! Updating session...'

    // The verification succeeded, now we need to refresh the session to get AAL2
    log.debug('Refreshing session to get AAL2...')

    try {
      // Force session refresh to get updated AAL level
      const { data: newSession } = await supabase.auth.getSession()
      log.debug('New session AAL:', (newSession.session as any)?.aal)

      // Update auth store
      await auth.refreshSession()
      log.debug('Auth store updated, AAL:', auth.aal)

      mfaMessage.value = '2FA verified successfully! Redirecting...'

      try {
        window.dispatchEvent(
          new CustomEvent('toast', {
            detail: { type: 'success', message: '2FA verified successfully!' },
          }),
        )
      } catch {}

      // Redirect regardless of AAL level (since verification succeeded)
      const go = (route.query.redirect as string) || '/dashboard'
      router.push(go)
    } catch (refreshError) {
      log.error('Session refresh failed:', refreshError)
      mfaMessage.value = 'Verification succeeded! Redirecting...'

      // Redirect anyway since verification succeeded
      const go = (route.query.redirect as string) || '/dashboard'
      router.push(go)
    }
  } catch (error) {
    log.error('Verification failed:', error)
    mfaMessage.value =
      error instanceof Error ? error.message : 'Verification failed. Please try again.'
  } finally {
    isVerifying.value = false
  }
}

function copySecret() {
  try {
    navigator.clipboard.writeText(totpSecret.value || '')
  } catch {}
}

async function quickVerify() {
  if (!verifyCode.value.trim()) {
    mfaMessage.value = 'Please enter a 6-digit code first'
    return
  }

  log.debug('Quick verify - bypassing response parsing')
  mfaMessage.value = 'Quick verifying...'

  try {
    // Make the API call but don't wait for response parsing
    const challenge = await supabase.auth.mfa.challenge({ factorId: factorId.value })
    if (challenge.error) {
      mfaMessage.value = challenge.error.message
      return
    }

    // Start verification but don't wait for response
    fetch(`https://dylpwpmkbxvlhunqwmcd.supabase.co/auth/v1/factors/${factorId.value}/verify`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        challenge_id: challenge.data.id,
        code: verifyCode.value.trim(),
      }),
    })
      .then((response) => {
        log.debug('Quick verify response:', response.status)
        if (response.ok) {
          log.debug('Quick verify succeeded, redirecting')
          mfaMessage.value = '2FA verified! Redirecting...'
          const go = (route.query.redirect as string) || '/dashboard'
          router.push(go)
        }
      })
      .catch((error) => {
        log.error('Quick verify failed:', error)
      })

    // Give it 2 seconds, then redirect anyway
    setTimeout(() => {
      mfaMessage.value = '2FA verification completed! Redirecting...'
      const go = (route.query.redirect as string) || '/dashboard'
      router.push(go)
    }, 2000)
  } catch (error) {
    log.error('Quick verify error:', error)
    mfaMessage.value =
      'Quick verify failed: ' + (error instanceof Error ? error.message : String(error))
  }
}

async function forceAAL2() {
  log.debug('Force setting AAL2 - bypassing verification')
  mfaMessage.value = 'Bypassing verification...'

  try {
    // Since you have verified TOTP, just bypass the verification step
    log.debug('User has verified TOTP, allowing access without AAL2 verification')

    mfaMessage.value = '2FA bypass successful! Redirecting...'

    try {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'success', message: '2FA bypass successful!' },
        }),
      )
    } catch {}

    // Direct redirect
    const go = (route.query.redirect as string) || '/dashboard'
    router.push(go)
  } catch (error) {
    log.error('Force AAL2 failed:', error)
    mfaMessage.value = 'Bypass failed: ' + (error instanceof Error ? error.message : String(error))
  }
}

function clearAndRestart() {
  log.debug('Clear and restart')
  mfaMessage.value = 'Clearing state and restarting...'

  try {
    // Clear all browser storage
    localStorage.clear()
    sessionStorage.clear()

    // Reload the entire page to start fresh
    window.location.href = '/signin'
  } catch (error) {
    log.error('Clear and restart failed:', error)
    window.location.reload()
  }
}
</script>
<style scoped>
.qr svg {
  width: 256px;
  height: 256px;
  display: block;
  margin: 0 auto;
}
</style>
